import * as RadixTooltip from '@radix-ui/react-tooltip'
import styles from './Tooltip.module.scss'
import classNames from 'classnames'

type TooltipProps = React.PropsWithChildren<{
  content: React.ReactNode
  /**
   * Tooltip can be controlled to always show, no matter if the
   * target element is hovered or not.
   */
  show?: boolean
  /**
   * Defaults to bottom
   */
  position?: 'top' | 'right' | 'bottom' | 'left'
  /**
   * When true, forces the tooltip to stay in the specified position even if it would render outside the viewport.
   * When false (default), the tooltip will automatically reposition to stay visible.
   */
  forcePosition?: boolean
  /**
   * We show an arrow by default but this can be disabled
   */
  arrow?: boolean
  style?: React.CSSProperties
  /**
   * Callback for when the tooltip is clicked outside.
   */
  onClickOutside?: () => void
}>

/**
 * Tooltip component that uses Radix UI's Tooltip.
 *
 * Wrap this around the component you want to show the tooltip for.
 *
 * Because of how Radix UI's Tooltip forwards props to the trigger element, you might need
 * to wrap the component you want to show the tooltip for in another element, like a span.
 *
 * @example
 * <Tooltip content="This is a tooltip">
 *   <span>
 *     <Button>Hover me</Button>
 *   </span>
 * </Tooltip>
 */
export const Tooltip = ({
  children,
  content,
  show,
  position = 'bottom',
  forcePosition = false,
  arrow = true,
  onClickOutside,
  style,
}: TooltipProps) => {
  return (
    <RadixTooltip.Provider delayDuration={100}>
      <RadixTooltip.Root open={show}>
        <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>

        <RadixTooltip.Portal>
          <RadixTooltip.Content
            collisionPadding={4}
            side={position}
            sideOffset={7}
            avoidCollisions={!forcePosition}
            onPointerDownOutside={() => onClickOutside?.()}
            className={classNames(styles.tooltip)}
            style={style}
          >
            {content}

            {arrow && <RadixTooltip.Arrow className={styles.arrow} />}
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  )
}
